/**
 * This module manages feature flags at the application level, allowing
 * developmental features to be turned on at runtime without code changes.
 *
 * Flags can be controlled via a UI in the app and URL parameters, and are
 * stored in session storage for persistence across page reloads.
 *
 * Flags can be enabled by default or hard-coded to `true`, but cannot be
 * toggled during an app session in the production environment
 *
 * @module featureFlags
 *
 * @example
 * // Add a new feature flag
 * const FEATURE_FLAG_CONFIG = [
 *   // ...existing flags,
 *   {
 *     variableName: 'newFeature',
 *     label: 'New Feature'
 *     description: 'A new feature that does stuff',
 *     enabledByDefault: false,
 *   }
 * ];
 *
 * import featureFlags from '#/shared/featureFlags';
 *
 * // `true` if feature is enabled
 * console.log(featureFlags.newFeature);
 *
 * // Turn on flags via URL
 * // `<app-path>?featureFlags=newFeature,anotherFeature`
 */

import { APP_HOSTNAMES } from '#/shared/constants';
import { getItem, storeItem } from '#/shared/utils/storage';

const envIsProd = () =>
  typeof window !== 'undefined' &&
  APP_HOSTNAMES.prod.some(host => host === window.location.hostname);

/**
 * Returns `true` if feature flag controls should be shown, controls are hidden
 * in instances that are shared with customers
 */
export const featureFlagControlsAllowed = (): boolean =>
  !envIsProd() &&
  APP_HOSTNAMES.beta.every(host => host !== window.location.hostname);

export const FEATURE_FLAGS_STORAGE_KEY = 'featureFlags';

interface FeatureFlag {
  /**
   * Name under which the feature status is exported from this module and key
   * used to enable it in the application url
   */
  variableName: string;
  /** Short, human-readable descriptor for the feature, used as label in UI */
  label: string;
  /** Expanded overview of the feature, presented as a description in UI */
  description: string;
  /**
   * If `true` the feature is turned on by default, otherwise it must be
   * enabled. This is applied when the app is loaded in a fresh tab, but if the
   * feature is turned off session storage will take precedence
   */
  enabledByDefault: boolean;
}

/**
 * Defines the set of feature flags that can be toggled at runtime. To create a
 * new feature flag add an object to this array.
 *
 * Flags here, together with hard-coded flags, determine the set of valid
 * feature flags for the application.
 */
export const FEATURE_FLAG_CONFIG = [
  {
    variableName: 'themes',
    label: 'Application Themes',
    description:
      'Enable a theme control in map settings that allows toggling between light, dark and legacy styling for the application',
    enabledByDefault: false,
  },
  {
    variableName: 'debugTools',
    label: 'Debug Tools',
    description:
      'Show developer debugging tools such as on-screen map controls for zoom/pitch/bearing and map padding visualization',
    enabledByDefault: false,
  },
  {
    variableName: 'thirdPartyVehicleTracking',
    label: 'Third Party Vehicles',
    description: 'Show third party vehicles on the map',
    enabledByDefault: false,
  },
] as const satisfies readonly FeatureFlag[];

/**
 * Keys for feature flags that are permanently enabled.
 *
 * Flags listed here are always `true`, ensuring code paths that support their
 * feature remain active while alternative paths are being removed. This array
 * should remain in the code even if empty.
 */
const HARD_CODED_FLAG_KEYS = [
  // TODO (PUBWEB-2449): Remove once Leaflet cleanup is complete
  'mapLibre',
  // TODO (PRJ77-1603): Remove once RTP functionality is stable in prod
  'regionalAgencies',
  // TODO (PRJ77-1604): Remove once leg fare functionality is stable in prod
  'legFares',
] as const;

export const FEATURE_FLAG_KEYS = [
  ...FEATURE_FLAG_CONFIG.map(f => f.variableName),
  ...HARD_CODED_FLAG_KEYS,
] as const;

export type SettableFeatureFlagKey =
  (typeof FEATURE_FLAG_CONFIG)[number]['variableName'];

type FeatureFlagKey = (typeof FEATURE_FLAG_KEYS)[number];

// Arbitrary keys are allowed here because, although flags always correspond
// to keys defined in this module, strict typing is difficult because the
// runtime object may include keys from session storage and URL params
export const setStoredFlags = (flags: Record<string, boolean>): void => {
  storeItem(FEATURE_FLAGS_STORAGE_KEY, flags, { session: true });
};

interface GetFeatureFlagParams {
  flagConfig: readonly FeatureFlag[];
  hardCodedFlagKeys: readonly string[];
}

/**
 * Returns a feature flags object containing all keys defined in `flagConfig`
 * and `hardCodedFlagKeys`.
 *
 * The returned object reflects the following order of precedence:
 * 1. Hard-coded flags are always `true`.
 * 2. Session storage values override the defaults.
 * 3. URL feature flags override session storage (except in SSR or prod).
 * 4. Defaults from `flagConfig`.
 *
 * At runtime, the application should call this function with the parameter
 * defaults; the optional `flagConfig` and `hardCodedFlagKeys` arguments exist only
 * to facilitate automated testing to validate correct behavior under various
 * configurations.
 *
 * @param flagConfig - Array of feature flag configurations
 * @param hardCodedFlagKeys - Array of flags that should always be `true`
 * @returns Read-only object mapping feature flag keys to `true` or `false`
 */
export const getFeatureFlags = ({
  flagConfig = FEATURE_FLAG_CONFIG,
  hardCodedFlagKeys = HARD_CODED_FLAG_KEYS,
}: Partial<GetFeatureFlagParams> = {}): Readonly<Record<string, boolean>> => {
  let featureLookup = Object.fromEntries(
    flagConfig.map(config => [config.variableName, config.enabledByDefault])
  );

  const hardCodedFlagsLookup = Object.fromEntries(
    hardCodedFlagKeys.map(flagName => [flagName, true])
  );

  // url params and local storage values are not permitted to turn on
  // feature flags in production, and they don't exist in SSR
  if (envIsProd() || typeof window === 'undefined') {
    return Object.freeze({
      ...featureLookup,
      ...hardCodedFlagsLookup,
    });
  }

  const { location, history } = window;
  const urlParams = new URLSearchParams(location.search);

  const validFlagKeys = flagConfig.map(config => config.variableName);
  const urlFeatureFlags =
    urlParams
      .get(FEATURE_FLAGS_STORAGE_KEY)
      ?.split(',')
      .filter(f => validFlagKeys.includes(f)) ?? [];

  const storedFeatureLookup = getItem(
    FEATURE_FLAGS_STORAGE_KEY,
    {},
    { session: true }
  );

  // features will be turned on if there is a flag for it in the url,
  // if it's set to `true` in session storage, or if its hard-coded
  featureLookup = {
    ...featureLookup,
    ...storedFeatureLookup,
    ...Object.fromEntries(urlFeatureFlags.map(f => [f, true])),
    ...hardCodedFlagsLookup,
  };

  if (urlFeatureFlags.length) {
    // store flags from  url in session storage so that they persist
    // when a browser tab is refreshed
    setStoredFlags(featureLookup);

    // remove the flags param from the url after it's read
    urlParams.delete(FEATURE_FLAGS_STORAGE_KEY);
    const paramsAsString = urlParams.toString();
    history.replaceState(
      null,
      '',
      location.pathname + (paramsAsString ? `?${paramsAsString}` : '')
    );
  }

  return Object.freeze(featureLookup);
};

/**
 * Validates that all expected feature flag keys are present on the supplied
 * object.
 *
 * This function return the feature flags object with a complete set of keys and
 * acts as a **type guard**. After calling it, TypeScript will know that
 * returned object contains all keys defined in `FEATURE_FLAG_KEYS`, enabling
 * full type-safe access and IDE autocompletion.
 *
 * Throws an error if any required keys are missing, to prevent failures and
 * unexpected behavior due to incomplete feature flags.
 *
 * @returns Feature flag object containing all required keys
 * @throws {Error} If any required feature flag keys are missing
 */
function getValidatedFeatureFlags(): Readonly<Record<FeatureFlagKey, boolean>> {
  const flags = getFeatureFlags();

  const missingKeys = FEATURE_FLAG_KEYS.filter(key => !(key in flags));
  if (missingKeys.length) {
    throw new Error(
      [
        `Feature flags object is missing key(s): ${missingKeys.join(', ')}.`,
        'All flags established in `FEATURE_FLAG_KEYS` must have a defined',
        'value in the resulting `featureFlag` object',
      ].join(' ')
    );
  }

  return flags;
}

/**
 * The application-wide feature flags object. These flags are initialized on
 * application load via and remain **static** thereafter.
 */
const featureFlags = getValidatedFeatureFlags();
export default featureFlags;
